import type { FC } from "react";
import { View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../../theme";
import { Text } from "../../Text";
import type { PaginationMenuPlacement } from "../Pagination.types";
import { PaginationPageSizeSelect } from "./PaginationPageSizeSelect";

export interface PaginationSummaryProps {
  readonly label: string;
  readonly totalText: string;
  readonly pageSize: number;
  readonly pageSizeOptions: readonly number[];
  readonly onPageSizeChange: (pageSize: number) => void;
  readonly showPageSizeSelect: boolean;
  readonly pageSizeLabel: string;
  readonly disabled: boolean;
  readonly menuPlacement: PaginationMenuPlacement;
}

/**
 * The "Showing 10 of 50" block.
 *
 * The page size sits inside the sentence rather than beside it, so the number
 * the user reads is the same control they change.
 */
const PaginationSummary: FC<PaginationSummaryProps> = ({
  label,
  totalText,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  showPageSizeSelect,
  pageSizeLabel,
  disabled,
  menuPlacement,
}) => (
  <View style={styles.container}>
    <Text textStyle="sm/regular" color="textMuted">
      {label}
    </Text>

    {showPageSizeSelect ?
      <PaginationPageSizeSelect
        value={pageSize}
        options={pageSizeOptions}
        onChange={onPageSizeChange}
        disabled={disabled}
        accessibilityLabel={pageSizeLabel}
        placement={menuPlacement}
      />
    : <Text textStyle="sm/medium">{pageSize.toString()}</Text>}

    <Text textStyle="sm/regular" color="textMuted">
      {totalText}
    </Text>
  </View>
);

PaginationSummary.displayName = "PaginationSummary";

export { PaginationSummary };

const styles = StyleSheet.create((theme) => ({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    // The select's menu escapes this row, so it must not be clipped by it.
    overflow: "visible",
  },
}));
